import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard, getOverdueInvoices } from '../../api/finance';

const fmt = (amount, currency = 'TRY') =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount ?? 0);

const StatCard = ({ label, value, sub, color = 'blue', icon }) => {
  const colors = {
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
    green:  'bg-green-50 border-green-200 text-green-700',
    red:    'bg-red-50 border-red-200 text-red-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
  };
  return (
    <div className={`rounded-xl border p-5 ${colors[color]}`}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-sm font-medium opacity-80">{label}</span>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-2xl font-bold tracking-tight">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-70">{sub}</p>}
    </div>
  );
};

export default function FinanceDashboard() {
  const [summary, setSummary] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getDashboard(), getOverdueInvoices()])
      .then(([d, o]) => { setSummary(d.data); setOverdue(o.data); })
      .catch(() => alert('Dashboard verisi yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
    </div>
  );

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finans Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Bu aya ait finansal özet</p>
        </div>
        <div className="flex gap-2">
          <Link to="/finance/invoices/new" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition">+ Yeni Fatura</Link>
          <Link to="/finance/expenses/new" className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition">+ Yeni Gider</Link>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Net Nakit (Bu Ay)" value={fmt(summary.net_cash)} sub={`Tahsilat: ${fmt(summary.total_collected)}`} color={summary.net_cash >= 0 ? 'green' : 'red'} icon="💰" />
          <StatCard label="Bekleyen Tahsilat" value={fmt(summary.pending_collections)} sub="Gönderilmiş faturalar" color="blue" icon="📨" />
          <StatCard label="Bekleyen Ödeme" value={fmt(summary.pending_payments)} sub="Onaylanmış giderler" color="orange" icon="📤" />
          <StatCard label="Bekleyen Onay" value={`${summary.pending_approvals} adet`} sub="Yönetim onayı gerekli" color={summary.pending_approvals > 0 ? 'red' : 'green'} icon="✅" />
        </div>
      )}

      {summary?.pending_approvals > 0 && (
        <Link to="/finance/approvals" className="block bg-amber-50 border border-amber-300 rounded-xl p-4 hover:bg-amber-100 transition">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <p className="font-semibold text-amber-800">{summary.pending_approvals} adet onay bekliyor</p>
              <p className="text-sm text-amber-600">Onay ekranına gitmek için tıklayın</p>
            </div>
            <span className="ml-auto text-amber-500">→</span>
          </div>
        </Link>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <span className="text-red-500">🔴</span> Gecikmiş Faturalar
            {overdue.length > 0 && <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">{overdue.length}</span>}
          </h2>
          <Link to="/finance/invoices?status=overdue" className="text-sm text-blue-600 hover:underline">Tümünü gör →</Link>
        </div>
        {overdue.length === 0 ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <p className="text-green-700 font-medium">🎉 Gecikmiş fatura yok!</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Fatura No</th>
                  <th className="px-4 py-3 text-right">Tutar</th>
                  <th className="px-4 py-3 text-right">Vade</th>
                  <th className="px-4 py-3 text-right">Gecikme</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {overdue.map((inv) => (
                  <tr key={inv.id} className="hover:bg-red-50 transition">
                    <td className="px-4 py-3 font-mono text-blue-600 font-medium">{inv.invoice_number}</td>
                    <td className="px-4 py-3 text-right font-semibold">{fmt(inv.total_amount, inv.currency)}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{new Date(inv.due_date).toLocaleDateString('tr-TR')}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold">{inv.days_overdue} gün</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { to: '/finance/invoices', label: 'Tüm Faturalar', icon: '🧾' },
          { to: '/finance/expenses', label: 'Giderler', icon: '💸' },
          { to: '/finance/approvals', label: 'Onaylar', icon: '✅' },
        ].map((item) => (
          <Link key={item.to} to={item.to} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3 hover:border-blue-300 hover:shadow-sm transition">
            <span className="text-2xl">{item.icon}</span>
            <span className="text-sm font-medium text-gray-700">{item.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
